import { Flex, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  ARCHITECT_NAME_MAX_LENGTH,
  isLeadSourceArchitect
} from "../../../../Lead/constants/lead-source-architect.ts"
import {
  LEAD_SOURCE_NONE,
  withNoneSourceOption
} from "../../../../Leads/constants/lead-source-none.ts"
import {
  optionRequiresDetail,
  pickableOptions
} from "../../../../../utils/select/managed-list-options.ts"
import type { ProjectFormData } from "../project-form-data.ts"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface SourceSectionProps {
  form: FormInstance<ProjectFormData>
  options: Record<string, BaseOptionType[]>
}

export function SourceSection({ form, options }: SourceSectionProps) {
  const { t } = useTranslation()
  const source = Form.useWatch("source", form)
  const showOtherReferral = optionRequiresDetail(source, options?.["source"])
  const showArchitectName = isLeadSourceArchitect(source)
  const sourceOptions = useMemo(
    () => pickableOptions(withNoneSourceOption(options?.["source"])),
    [options]
  )

  return (
    <Form.Item label={"Source"} className="mb-0">
      <Flex gap={8} align="flex-start" className="w-full">
        <Form.Item<ProjectFormData>
          name="source"
          noStyle
          className="mb-0 min-w-[180px] flex-1"
          rules={[
            {
              required: false,
              message: "Please select source"
            }
          ]}
          normalize={(value: string) => value ?? LEAD_SOURCE_NONE}
        >
          <ResolvedSelect
            allowClear
            placeholder="Select Source"
            options={sourceOptions}
            showSearch
            className="min-w-[180px] flex-1"
            onChange={() => {
              form.setFieldValue("source_comment", null)
              form.setFieldValue("architect_name", null)
              form.validateFields(["source_comment", "architect_name"])
            }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item<ProjectFormData>
          hidden={!showOtherReferral}
          name="source_comment"
          dependencies={["source"]}
          className="mb-0 min-w-[200px] flex-1 max-w-full"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const shouldValidate = optionRequiresDetail(
                  getFieldValue("source"),
                  options?.["source"]
                )

                if (!shouldValidate) return Promise.resolve()

                if (!value || !value.trim()) {
                  return Promise.reject("Please add source details")
                }

                if (value && value.length > 255) {
                  return Promise.reject(
                    "Source details field should be no more than 255 characters."
                  )
                }
                return Promise.resolve()
              }
            })
          ]}
        >
          <TextInput
            placeholder="Source details (e.g. who referred, which campaign)"
            className="min-w-[200px] flex-1 max-w-full"
            maxLength={255}
          />
        </Form.Item>
        <Form.Item<ProjectFormData>
          hidden={!showArchitectName}
          name="architect_name"
          dependencies={["source"]}
          className="mb-0 min-w-[200px] flex-1 max-w-full"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!isLeadSourceArchitect(getFieldValue("source"))) {
                  return Promise.resolve()
                }
                if (!value || !value.trim()) {
                  return Promise.reject(t("leadWizard.project.architectNameRequired"))
                }
                if (value.length > ARCHITECT_NAME_MAX_LENGTH) {
                  return Promise.reject(t("leadWizard.project.architectNameTooLong"))
                }
                return Promise.resolve()
              }
            })
          ]}
        >
          <TextInput
            placeholder={t("leadWizard.project.architectNamePlaceholder")}
            className="min-w-[200px] flex-1 max-w-full"
            maxLength={ARCHITECT_NAME_MAX_LENGTH}
          />
        </Form.Item>
      </Flex>
    </Form.Item>
  )
}
